import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";

const guides = [
  {
    icon: "ri-compass-3-line",
    title: "按分类浏览",
    desc: "顶部导航可直接进入电影、电视剧、综艺分类页，左侧筛选区支持按地区、年份、类型与评分区间缩小范围。",
  },
  {
    icon: "ri-sort-desc",
    title: "排序与分页",
    desc: "列表页可按发布时间、热度或评分排序，并通过底部分页控件快速跳转到指定页。",
  },
  {
    icon: "ri-search-line",
    title: "关键词搜索",
    desc: "在搜索页输入关键词，可同时匹配影视标题、剧情简介、导演、类型与标签，快速定位想看的内容。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "专题合集",
    desc: "专题页按主题打包片单，附带推荐语，适合选择困难时直接开看。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - WW我的快乐在哪里影视资讯",
    description:
      "了解 WW我的快乐在哪里 的网站定位、内容方向、联系方式与使用说明，我们专注提供影视资讯、剧情介绍与影评推荐。",
    keywords: "关于我们,WW我的快乐在哪里,影视资讯,联系方式,使用说明",
    path: "https://db6pnwez.cn/about",
  });

  return (
    <section className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-5 max-w-3xl">
        <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">关于我们</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
          WW我的快乐在哪里，是一个为影视爱好者而生的资讯与剧情介绍门户。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-8">
          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950">网站定位</h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                我们相信，看什么、怎么看，本身就是一件值得被认真对待的事。WW我的快乐在哪里围绕
                “影视资讯 + 剧情介绍”这一主线，为观众整理最新动态、剧情解析与影评分析，
                覆盖电影、电视剧与综艺等多元内容。我们不做播放与下载，只专注于把作品讲清楚，
                让你在打开第一集之前，就已经知道自己会看到什么。
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { value: "每日", label: "资讯动态更新" },
                  { value: "多元", label: "电影电视剧综艺" },
                  { value: "深度", label: "剧情与影评解析" },
                ].map((item) => (
                  <div key={item.label} className="rounded-md bg-background-100 px-4 py-3">
                    <p className="text-base font-black text-primary-600">{item.value}</p>
                    <p className="mt-1 text-xs text-foreground-600">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950">使用说明</h2>
              <p className="mt-2 text-sm text-foreground-600">四步快速找到你想看的内容。</p>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {guides.map((guide) => (
                  <div key={guide.title} className="flex gap-4">
                    <span
                      className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-primary-100 text-primary-600"
                      aria-hidden="true"
                    >
                      <i className={`${guide.icon} text-xl`} />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950">{guide.title}</h3>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{guide.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <aside className="flex flex-col gap-6">
          <Reveal>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="text-base font-bold text-foreground-950">联系方式</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你有内容合作、纠错建议或使用反馈，欢迎通过邮件与我们联系。
              </p>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-mail-send-line" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">内容合作</span>
                    <a
                      href="mailto:hello@wwkuaile.com"
                      className="font-medium text-primary-600 hover:text-accent-600"
                    >
                      hello@wwkuaile.com
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-feedback-line" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">意见反馈</span>
                    <a
                      href="mailto:feedback@wwkuaile.com"
                      className="font-medium text-primary-600 hover:text-accent-600"
                    >
                      feedback@wwkuaile.com
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-background-200 text-foreground-700">
                    <i className="ri-time-line" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">回复时间</span>
                    <span className="font-medium text-foreground-800">工作日 09:30 - 18:30</span>
                  </span>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-lg border border-background-200 bg-primary-950 p-6 text-background-100">
              <h2 className="text-base font-bold text-background-50">快速开始</h2>
              <p className="mt-3 text-sm leading-relaxed text-primary-200">
                不确定从哪看起？先去专题合集挑一个感兴趣的主题，或者直接搜索关键词。
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <Link
                  to="/special"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 whitespace-nowrap"
                >
                  <i className="ri-stack-line" aria-hidden="true" />
                  浏览专题合集
                </Link>
                <Link
                  to="/search"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-50/30 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap"
                >
                  <i className="ri-search-line" aria-hidden="true" />
                  搜索影视内容
                </Link>
              </div>
            </div>
          </Reveal>
        </aside>
      </div>
    </section>
  );
}